import React, { useState } from "react";
import { parseLocalDate } from "utils/dateUtil";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { IconButton, Chip, LinearProgress, Box, Typography, Tooltip } from "@mui/material";
import { Edit, Delete, Person, Warning } from "components/icons";

const getAllocationColor = (allocation) => {
  if (allocation > 100) return "error";
  if (allocation >= 80) return "warning";
  if (allocation >= 50) return "info";
  return "success";
};

const getUtilizationColor = (utilization) => {
  if (utilization >= 90) return "success";
  if (utilization >= 70) return "info";
  if (utilization >= 50) return "warning";
  return "error";
};

export const ResourceDataGrid = ({ resources, isLoading, onEdit, onDelete, showActions = true }) => {
  const [pageSize, setPageSize] = useState(10);

  const columns = [
    ...(showActions ? [{
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <IconButton key="edit" onClick={() => onEdit(params.row)} color="primary" size="small">
          <Edit />
        </IconButton>,
        <IconButton key="delete" onClick={() => onDelete(params.row)} color="error" size="small">
          <Delete />
        </IconButton>,
      ],
    }] : []),
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "memberName",
      headerName: "Member",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Person fontSize="small" />
          <span>{params.value}</span>
        </Box>
      ),
    },
    { field: "projectName", headerName: "Project", width: 150 },
    { field: "role", headerName: "Role", width: 130 },
    {
      field: "allocationPercentage",
      headerName: "Allocation",
      width: 140,
      renderCell: (params) => (
        <Tooltip title={params.value > 100 ? "Over-allocated!" : `${params.value}% allocated`}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
            {params.value > 100 && <Warning color="error" fontSize="small" sx={{ mr: 0.5 }} />}
            <Box sx={{ width: "70%", mr: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(params.value || 0, 100)}
                color={getAllocationColor(params.value)}
              />
            </Box>
            <Typography variant="body2" color={params.value > 100 ? "error" : "text.secondary"}>
              {`${params.value || 0}%`}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "startDate",
      headerName: "Start",
      width: 110,
      valueFormatter: (value) => value ? parseLocalDate(value).toLocaleDateString() : "N/A",
    },
    {
      field: "endDate",
      headerName: "End",
      width: 110,
      valueFormatter: (value) => value ? parseLocalDate(value).toLocaleDateString() : "N/A",
    },
    {
      field: "utilizationRate",
      headerName: "Utilization",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={`${params.value || 0}%`}
          color={getUtilizationColor(params.value)}
          size="small"
        />
      ),
    },
  ];

  return (
    <MDBox pt={3} py={3} px={2} height={400}>
      <DataGrid
        rows={resources || []}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20, 50]}
        loading={isLoading}
        disableSelectionOnClick
        getRowId={(row) => row.id || row.allocationId}
      />
    </MDBox>
  );
};

ResourceDataGrid.propTypes = {
  resources: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  showActions: PropTypes.bool,
};

export default ResourceDataGrid;

